import type { ReactNode } from "react";
import { ROUTES } from "../constants";
import { PublicRoute } from "../components/routes/PublicRoute";
import { ProtectedRoute } from "../components/routes/ProtectedRoute";
import { CompanyOnlyRoute } from "../components/routes/CompanyOnlyRoute";
import {
  PermissionRoute,
  CompanyFormPermissionRoute,
  CompanyProfileRoute,
} from "../components/routes/PermissionRoute";
import { PageId, ActionCode } from "../types/index";
import { UpgradePaymentRedirect } from "../components/routes/UpgradePaymentRedirect";
import {
  LoginPage,
  ForgotPasswordPage,
  CompanyFormPage,
  DashboardPage,
  EmployeesPage,
  EmployeeFormPage,
  EmployeeScansPage,
  PublicEmployeePage,
  PublicIndividualPage,
  CompaniesPage,
  CompanyScansPage,
  IndividualsPage,
  IndividualScansPage,
  PackageFormPage,
  PackagesPage,
  UsersPage,
  UserFormPage,
  LicensesPage,
  InvoicesPage,
  LookupsPage,
  LookupFormPage,
  ContactUsPage,
  ContactUsFormPage,
  HomePage,
  ContactPage,
  AboutPage,
  FeaturesPage,
  PricingPage,
  FaqPage,
  PrivacyPage,
  DataPolicyPage,
  TermsPage,
  DemoPage,
  PaymentResultPage,
  UpgradePackagePage,
  RenewPackagePage,
  NotFoundPage,
} from "./lazyPages";

export type AppRouteDefinition = {
  path: string;
  element: ReactNode;
};

export const appRoutes: AppRouteDefinition[] = [
  {
    path: ROUTES.LOGIN,
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.FORGOTPASSWORD_WITH_ID,
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.FORGOTPASSWORD,
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <CompanyFormPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.REGISTER_Package,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <CompanyFormPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.USER_VIEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.USERS}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.USERS}
          entityPath={ROUTES.USERS}
        >
          <UserFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.USER_NEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.USERS}
          actionCode={ActionCode.ADD}
          fallbackPath={ROUTES.USERS}
        >
          <UserFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.USER_EDIT,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.USERS}
          actionCode={ActionCode.EDIT}
          fallbackPath={ROUTES.USERS}
          entityPath={ROUTES.USERS}
        >
          <UserFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEE_VIEW,
    element: (
      <CompanyOnlyRoute>
        <PermissionRoute
          pageId={PageId.EMPLOYEES}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.EMPLOYEES}
          entityPath={ROUTES.EMPLOYEES}
        >
          <EmployeeFormPage />
        </PermissionRoute>
      </CompanyOnlyRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEE_SCANS,
    element: (
      <CompanyOnlyRoute>
        <PermissionRoute
          pageId={PageId.EMPLOYEES}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.EMPLOYEES}
        >
          <EmployeeScansPage />
        </PermissionRoute>
      </CompanyOnlyRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEE_PUBLIC,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <PublicEmployeePage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.INDIVIDUAL_PUBLIC,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <PublicIndividualPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEE_NEW,
    element: (
      <CompanyOnlyRoute>
        <PermissionRoute
          pageId={PageId.EMPLOYEES}
          actionCode={ActionCode.ADD}
          fallbackPath={ROUTES.EMPLOYEES}
        >
          <EmployeeFormPage />
        </PermissionRoute>
      </CompanyOnlyRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEE_EDIT,
    element: (
      <CompanyOnlyRoute>
        <PermissionRoute
          pageId={PageId.EMPLOYEES}
          actionCode={ActionCode.EDIT}
          fallbackPath={ROUTES.EMPLOYEES}
          entityPath={ROUTES.EMPLOYEES}
        >
          <EmployeeFormPage />
        </PermissionRoute>
      </CompanyOnlyRoute>
    ),
  },
  {
    path: ROUTES.COMPANY_VIEW,
    element: (
      <ProtectedRoute>
        <CompanyFormPermissionRoute actionCode={ActionCode.VIEW}>
          <CompanyFormPage />
        </CompanyFormPermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.COMPANY_NEW,
    element: (
      <ProtectedRoute>
        <CompanyFormPermissionRoute actionCode={ActionCode.ADD}>
          <CompanyFormPage />
        </CompanyFormPermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.COMPANY_EDIT,
    element: (
      <ProtectedRoute>
        <CompanyFormPermissionRoute actionCode={ActionCode.EDIT}>
          <CompanyFormPage />
        </CompanyFormPermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.COMPANY_PROFILE,
    element: (
      <ProtectedRoute>
        <CompanyProfileRoute>
          <CompanyFormPage />
        </CompanyProfileRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.MY_PROFILE,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.MY_PROFILE}
          actionCode={ActionCode.VIEW}
        >
          <CompanyFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.PACKAGE_VIEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.PACKAGES}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.PACKAGES}
          entityPath={ROUTES.PACKAGES}
        >
          <PackageFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.PACKAGE_NEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.PACKAGES}
          actionCode={ActionCode.ADD}
          fallbackPath={ROUTES.PACKAGES}
        >
          <PackageFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.PACKAGE_EDIT,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.PACKAGES}
          actionCode={ActionCode.EDIT}
          fallbackPath={ROUTES.PACKAGES}
          entityPath={ROUTES.PACKAGES}
        >
          <PackageFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.DASHBOARD,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.DASHBOARD}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.HOME}
        >
          <DashboardPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.UPGRADE_PACKAGE,
    element: (
      <ProtectedRoute>
        <UpgradePackagePage />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.RENEW_PACKAGE,
    element: (
      <ProtectedRoute>
        <RenewPackagePage />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.EMPLOYEES,
    element: (
      <CompanyOnlyRoute>
        <PermissionRoute
          pageId={PageId.EMPLOYEES}
          actionCode={ActionCode.VIEW}
        >
          <EmployeesPage />
        </PermissionRoute>
      </CompanyOnlyRoute>
    ),
  },
  {
    path: ROUTES.COMPANIES,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.COMPANIES}
          actionCode={ActionCode.VIEW}
        >
          <CompaniesPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.COMPANY_SCANS,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.COMPANIES}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.COMPANIES}
        >
          <CompanyScansPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.INDIVIDUALS,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.INDIVIDUALS}
          actionCode={ActionCode.VIEW}
        >
          <IndividualsPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.INDIVIDUAL_SCANS,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.INDIVIDUALS}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.INDIVIDUALS}
        >
          <IndividualScansPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.PACKAGES,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.PACKAGES}
          actionCode={ActionCode.VIEW}
        >
          <PackagesPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.USERS,
    element: (
      <ProtectedRoute>
        <PermissionRoute pageId={PageId.USERS} actionCode={ActionCode.VIEW}>
          <UsersPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.LICENSES,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.LICENSES}
          actionCode={ActionCode.VIEW}
        >
          <LicensesPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.INVOICES,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.INVOICES}
          actionCode={ActionCode.VIEW}
        >
          <InvoicesPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.LOOKUPS,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.LOOKUPS}
          actionCode={ActionCode.VIEW}
        >
          <LookupsPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.LOOKUP_NEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.LOOKUPS}
          actionCode={ActionCode.ADD}
          fallbackPath={ROUTES.LOOKUPS}
        >
          <LookupFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.LOOKUP_EDIT,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.LOOKUPS}
          actionCode={ActionCode.EDIT}
          fallbackPath={ROUTES.LOOKUPS}
        >
          <LookupFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.HOME,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <HomePage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.CONTACTUS,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.CONTACTUS}
          actionCode={ActionCode.VIEW}
        >
          <ContactUsPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.CONTACTUS_VIEW,
    element: (
      <ProtectedRoute>
        <PermissionRoute
          pageId={PageId.CONTACTUS}
          actionCode={ActionCode.VIEW}
          fallbackPath={ROUTES.CONTACTUS}
        >
          <ContactUsFormPage />
        </PermissionRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.CONTACT,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <ContactPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.ABOUT,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <AboutPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.DEMO,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <DemoPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.FEATURES,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <FeaturesPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.PRICING,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <PricingPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.FAQ,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <FaqPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.PRIVACY,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <PrivacyPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.DATA_POLICY,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <DataPolicyPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.TERMS,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <TermsPage />
      </PublicRoute>
    ),
  },
  {
    path: "/paymentResult",
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <PaymentResultPage />
      </PublicRoute>
    ),
  },
  {
    path: ROUTES.UPGRADE_PAYMENT_WITH_ID,
    element: (
      <ProtectedRoute>
        <UpgradePaymentRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.UPGRADE_PAYMENT,
    element: (
      <ProtectedRoute>
        <UpgradePaymentRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.NOT_FOUND,
    element: (
      <PublicRoute redirectIfAuthenticated={false}>
        <NotFoundPage />
      </PublicRoute>
    ),
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];
